import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { LOJ_COLORS } from '../theme/lojTheme';
import OrnamentalDivider from './OrnamentalDivider';
import { useTranslation } from '../i18n/LanguageContext';

const ACCENT = LOJ_COLORS.ember;

const Checklist: React.FC<{ items: string[] }> = ({ items }) => {
  const { t } = useTranslation();
  return (
  <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
    {items.map((item) => (
      <Box
        component="li"
        key={item}
        sx={{
          color: LOJ_COLORS.parchmentLight,
          fontFamily: "'Crimson Text', Georgia, serif",
          fontSize: '1.08rem',
          lineHeight: 1.55,
          '&::marker': { color: ACCENT },
        }}
      >
        {t(item)}
      </Box>
    ))}
  </Box>
  );
};

const Callout: React.FC<{ label: string; children: React.ReactNode; color?: string }> = ({
  label,
  children,
  color = LOJ_COLORS.goldBright,
}) => (
  <Box
    sx={{
      mt: 2.5,
      p: 2,
      background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(LOJ_COLORS.void, 0.4)})`,
      border: `1px solid ${alpha(color, 0.35)}`,
      borderLeft: `3px solid ${color}`,
    }}
  >
    <Typography
      sx={{
        mb: 0.75,
        color,
        fontFamily: "'Cinzel', serif",
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color: LOJ_COLORS.parchmentLight, fontFamily: "'Crimson Text', Georgia, serif", fontSize: '1.05rem', lineHeight: 1.6 }}>
      {children}
    </Typography>
  </Box>
);

const TrialCagePanel: React.FC<{ hidden: boolean }> = ({ hidden }) => {
  const { t } = useTranslation();
  return (
  <Box sx={{ display: hidden ? 'none' : 'block', animation: hidden ? 'none' : 'fadeIn 0.35s ease' }}>
    <Box
      sx={{
        position: 'relative',
        mb: 4,
        pt: 5,
        pb: 4,
        px: { xs: 3, md: 5 },
        background: `linear-gradient(180deg, ${alpha(LOJ_COLORS.void, 0.4)} 0%, ${alpha(ACCENT, 0.05)} 50%, transparent 100%)`,
        borderBottom: `1px solid ${alpha(ACCENT, 0.25)}`,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '2rem', md: '2.8rem', lg: '3.2rem' },
          color: ACCENT,
          textShadow: `0 0 20px ${alpha(LOJ_COLORS.ember, 0.55)}, 0 2px 4px ${alpha(LOJ_COLORS.void, 0.9)}`,
          mb: 1,
        }}
      >
        Trial Cage
      </Typography>
      <Typography variant="subtitle1" sx={{ color: LOJ_COLORS.parchment, letterSpacing: '0.2em', fontSize: '0.8rem', opacity: 0.8 }}>
        — {t('Rally Discipline · Alliance Damage · Better Rewards')} —
      </Typography>
    </Box>

    <Box sx={{ px: { xs: 2, md: 4 } }}>
      <OrnamentalDivider label={t('Rally Starters')} color={ACCENT} />
      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.bloodDark, 0.45)}, ${alpha(LOJ_COLORS.darkStone, 0.9)})`,
          border: `1px solid ${alpha(ACCENT, 0.35)}`,
          borderTop: `3px solid ${ACCENT}`,
        }}
      >
        <Typography variant="h4" sx={{ mb: 2.5, fontSize: '1.15rem', color: LOJ_COLORS.ember }}>
          ⚔ {t('Seat Players & Rally Leads')}
        </Typography>
        <Checklist
          items={[
            'Yard lineup: Scorpion, Pharmacist, and Mind Reader on rally starters.',
            'Boosters work but are expensive. Do not use Mia or blue heroes.',
            'Save a true rally formation focused entirely on attack, lethality, and damage: for example Tyron/Phoenix, Vivien/Alph, Devilian, and Epics such as Lofili.',
            'Use 70,000 troops total: absolutely no Shieldbearers; 0–10% Bombers; the rest Shooters plus your best Robot.',
            'If needed, reset your Robot and invest fully in one Robot with cores. Bomber lethality is useful; Shield HP is not needed.',
            'Manage your own rally: select the best four boosters and place them in positions 1–4.',
            'Remove any joiner sending more than 70,000 troops.',
          ]}
        />
        <Callout label={t('FORMATION RULE')} color={LOJ_COLORS.goldBright}>
          {t('Your rally starter lineup supplies the main damage. Build it for maximum offensive power before opening rallies.')}
        </Callout>
      </Box>

      <OrnamentalDivider label={t('Rally Joiners')} color={LOJ_COLORS.crystalLight} />
      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.crystalDeep, 0.5)}, ${alpha(LOJ_COLORS.darkStone, 0.9)})`,
          border: `1px solid ${alpha(LOJ_COLORS.crystalLight, 0.35)}`,
          borderTop: `3px solid ${LOJ_COLORS.crystalLight}`,
        }}
      >
        <Typography variant="h4" sx={{ mb: 2.5, fontSize: '1.15rem', color: LOJ_COLORS.crystalLight }}>
          🛡 {t('Every March Matters')}
        </Typography>
        <Checklist
          items={[
            'Save every march you plan to send. One hero is enough, provided it is offensive and has a strong first Warskill: attack, lethality, or damage.',
            'Recommended examples: Lofili, Lunarl, Flameborn, Samiri, Ekko, and selected legendary heroes.',
            'Use exactly 70,000 troops. No Shieldbearers: Bombers and Shooters only, or Shooters only when possible.',
            'Save these formations in the Troop Formations menu. That is exactly why the feature exists.',
            'Do not put Mia or blue heroes in the first slot.',
          ]}
        />
        <Callout label={t('WHY THE FIRST FOUR SLOTS MATTER')} color={LOJ_COLORS.crystalLight}>
          {t('The starter lineup provides the primary damage. The first four joiners boost it through their first hero and first Warskill; that is the only joiner contribution that matters.')}
        </Callout>
      </Box>

      <OrnamentalDivider label={t('Alliance Doctrine')} color={LOJ_COLORS.goldBright} />
      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.void, 0.85)}, ${alpha(LOJ_COLORS.darkStone, 0.9)})`,
          border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.28)}`,
        }}
      >
        <Checklist
          items={[
            'The 70,000 troop limit lets more members join every rally and gives leaders more choices for the first four booster slots.',
            'More participating players create higher alliance damage, which means better rewards for everyone.',
            'Rallies will not fill in seconds, giving everyone time to join without panic.',
            'VIP 6 provides a second formation bar. Save the correct rally and joiner lineups so you cannot accidentally send the wrong march.',
            'Legendary heroes are not automatically better. The first Warskill and the assigned slot are what matter.',
            'Trial Cage returns every second day. Spend five minutes saving the formations once, then use them consistently.',
          ]}
        />
        <Callout label={t('FINAL REMINDER')} color={LOJ_COLORS.ember}>
          {t('Do not panic. Use your saved formations, respect the troop limit, and let the first four booster positions carry the rally.')}
        </Callout>
      </Box>

      <OrnamentalDivider label={t('Video Guides')} color={LOJ_COLORS.parchment} />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        {[
          ['Trial Cage Guide 1', 'https://youtu.be/Yo_UJUHA7xo?is=nu5e5HwXm64FrtAF'],
          ['Trial Cage Guide 2', 'https://youtu.be/Z52PtxzLTOs?is=YZDn7CJYBRCyRuMv'],
        ].map(([label, href]) => (
          <Box
            key={href}
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
              flex: '1 1 220px',
              p: 2,
              color: LOJ_COLORS.goldGlow,
              textDecoration: 'none',
              textAlign: 'center',
              fontFamily: "'Cinzel', serif",
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              background: alpha(LOJ_COLORS.darkStone, 0.9),
              border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.35)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: alpha(LOJ_COLORS.goldDim, 0.3),
                borderColor: LOJ_COLORS.goldBright,
                boxShadow: `0 0 16px ${alpha(LOJ_COLORS.goldGlow, 0.15)}`,
              },
            }}
          >
            ▶ {t(label)}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
  );
};

export default TrialCagePanel;
